import {
  addDays,
  addMonths,
  differenceInHours,
  getDaysInMonth,
  isBefore,
  isSameMonth,
  parseISO,
  startOfMonth,
} from "date-fns";
import type { PointerEvent } from "react";

import type { CaptainBillingAccount, CaptainTokenBalance } from "./types";

export const capLeftAfter = (balance: CaptainTokenBalance) =>
  Math.max(0, balance.monthlyCapTokens - balance.monthlyCapUsedTokens);

export const availableAfter = (balance: CaptainTokenBalance) => capLeftAfter(balance) + balance.topUpBalanceTokens;

const clampPercent = (value: number) => Math.min(Math.max(value, 0), 100);

export const capUsedPercent = (account: Pick<CaptainBillingAccount, "monthlyCapTokens" | "capLeftTokens">) =>
  account.monthlyCapTokens > 0
    ? clampPercent(((account.monthlyCapTokens - account.capLeftTokens) / account.monthlyCapTokens) * 100)
    : 0;

type BalanceParts = Pick<CaptainBillingAccount, "monthlyCapTokens" | "capLeftTokens" | "topUpBalanceTokens">;

export function availableShares(account: BalanceParts, spentFromTopUp: number) {
  const topUp = Math.max(0, account.topUpBalanceTokens);
  const capacity = account.monthlyCapTokens + topUp + Math.max(0, spentFromTopUp);
  const percent = (tokens: number) => (capacity > 0 ? clampPercent((tokens / capacity) * 100) : 0);
  const topUpShare = percent(topUp);
  return { topUp: topUpShare, cap: Math.min(percent(Math.max(0, account.capLeftTokens)), 100 - topUpShare) };
}

export const MIN_TRACK_SHARE = 0.2;

export function balanceTracks(account: BalanceParts, spentFromTopUp: number) {
  const capUsed = Math.max(0, account.monthlyCapTokens - Math.max(0, account.capLeftTokens));
  const topUpSpent = Math.max(0, spentFromTopUp);
  const topUpTotal = Math.max(0, account.topUpBalanceTokens) + topUpSpent;
  const capacity = account.monthlyCapTokens + topUpTotal;
  const share = (part: number) => (capacity > 0 ? Math.max(part / capacity, MIN_TRACK_SHARE) : 0);
  return {
    capacity,
    usedPercent: capacity > 0 ? clampPercent(((capUsed + topUpSpent) / capacity) * 100) : 0,
    cap: account.monthlyCapTokens > 0
      ? { share: share(account.monthlyCapTokens), usedPercent: clampPercent((capUsed / account.monthlyCapTokens) * 100) }
      : null,
    topUp: topUpTotal > 0 ? { share: share(topUpTotal), usedPercent: clampPercent((topUpSpent / topUpTotal) * 100) } : null,
  };
}

const elapsedDays = (monthStart: Date, now: Date) => Math.max(differenceInHours(now, monthStart) / 24, 1);
const nextMonthStart = (monthStart: Date) => startOfMonth(addMonths(monthStart, 1));

export function capRunOut(
  account: Pick<CaptainBillingAccount, "month" | "monthlyCapTokens" | "capLeftTokens">,
  now: Date,
): Date | "lasts" | null {
  const monthStart = parseISO(account.month);
  if (!isSameMonth(monthStart, now) || account.monthlyCapTokens <= 0) return null;
  const used = account.monthlyCapTokens - Math.max(0, account.capLeftTokens);
  if (used <= 0) return null;
  if (account.capLeftTokens <= 0) return null;

  const runOut = addDays(monthStart, (account.monthlyCapTokens / used) * elapsedDays(monthStart, now));
  return isBefore(runOut, nextMonthStart(monthStart)) ? runOut : "lasts";
}

export function balanceRunOut(
  account: Pick<CaptainBillingAccount, "month" | "availableTokens">,
  usedThisMonth: number,
  now: Date,
): Date | "lasts" | null {
  const monthStart = parseISO(account.month);
  if (!isSameMonth(monthStart, now) || usedThisMonth <= 0 || account.availableTokens <= 0) return null;

  const perDay = usedThisMonth / elapsedDays(monthStart, now);
  const runOut = addDays(now, account.availableTokens / perDay);
  return isBefore(runOut, nextMonthStart(monthStart)) ? runOut : "lasts";
}

export function projectMonth(usedSoFar: number, month: string, now: Date): number {
  const monthStart = parseISO(month);
  if (!isSameMonth(monthStart, now) || usedSoFar <= 0) return usedSoFar;
  return Math.round((usedSoFar / elapsedDays(monthStart, now)) * getDaysInMonth(monthStart));
}

export const topUpAfterCorrection = (topUpBalanceTokens: number, tokens: number) =>
  Math.max(0, topUpBalanceTokens + tokens);

export const capResetDate = (month: string) => addMonths(parseISO(month), 1);

export const isHoverPointer = (event: PointerEvent) => event.pointerType !== "touch" && event.pointerType !== "pen";
